import useWindowSize from "@DSG/Common/Hooks/useWindowSize";
import colorPallete, { bodyFonts, elevation, headlineFonts, labelFonts } from "@DSG/MobiusOverrides/styles/variables";
import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasProduct, withProduct } from "@insite/client-framework/Components/ProductContext";
import Zone from "@insite/client-framework/Components/Zone";
import { getPageLinkByPageType, useGetLink } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import { WidgetDefinition } from "@insite/client-framework/Types/ContentItemDefinitions";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import { GridContainerProps } from "@insite/mobius/GridContainer";
import { GridItemProps } from "@insite/mobius/GridItem";
import Img from "@insite/mobius/Img/Img";
import { Button, GridContainer, GridItem, Link } from "@insite/mobius/index";
import Typography from "@insite/mobius/Typography";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { lab } from "color";
import parse from "html-react-parser";
import * as React from "react";
import styled, { css } from "styled-components";

type Props = OwnProps & HasProduct;
// this is used to help ensure that the names match between props.fields and definition.fieldDefinitions[].name
const enum fields {
    quickLinksTitle = "quickLinksTitle",
    needHelpTitle = "needHelpTitle",
    needHelpCTALink = "needHelpCTALink",
    needHelpCTALabel = "needHelpCTALabel",
    openInNewWindow = "openInNewWindow",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.quickLinksTitle]: boolean;
        [fields.needHelpTitle]: boolean;
        [fields.needHelpCTALink]: LinkFieldValue;
        [fields.needHelpCTALabel]: string;
        [fields.openInNewWindow]: boolean;
    };
}

export interface DsgQuickLinksSectionWidgetStyles {
    wrapper?: InjectableCss;
    columnGridContainer?: GridContainerProps;
    leftColumnGridItem?: GridItemProps;
    rightColumnGridItem?: GridItemProps;
    quickLinksWrapper?: InjectableCss;
    sectionHeading?: InjectableCss;
    needHelpWrapper?: InjectableCss;
    needHelpCta?: InjectableCss;
}

export const dsgQuickLinksSectionWidgetStyles: DsgQuickLinksSectionWidgetStyles = {
    wrapper: {
        css: css`
            background: ${colorPallete.grey50};
            border: 1px solid ${colorPallete.grey500};
            border-radius: 8px;
            padding: 40px 24px;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        null,
                        css`
                            padding: 24px;
                            margin-top: 48px;
                        `,
                        css`
                            padding: 64px;
                        `,
                    ],
                    "min",
                )}
            @media screen and (min-width: 1346px) {
                padding: 80px;
            }
        `,
    },
    columnGridContainer: {
        gap: 40,
    },
    leftColumnGridItem: {
        width: [12, 12, 6, 8, 8],
        css: css`
            flex-direction: column;
            row-gap: 40px;
            > div {
                width: 100%;
            }
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        null,
                        css`
                            padding-right: 0;
                        `,
                    ],
                    "min",
                )}
        `,
    },
    rightColumnGridItem: {
        width: [12, 12, 6, 4, 4],
        css: css`
            > div {
                display: flex;
                flex-direction: column;
                width: 100%;
                row-gap: 24px;
                align-items: center;
                > div {
                    width: 100%;
                }
            }
            .dsgBtn {
                width: 100%;
            }
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        null,
                        css`
                            padding-left: 40px;
                            > div {
                                row-gap: 40px;
                            }
                        `,
                    ],
                    "min",
                )}
        `,
    },
    quickLinksWrapper: {
        css: css`
            > div {
                display: flex;
                flex-wrap: wrap;
                flex-direction: row;
                gap: 40px;
                > div {
                    width: calc(50% - 20px);
                }
            }
            .list-wrapper {
                padding: 0;
                text-transform: initial;
                row-gap: 16px;
                display: flex;
                flex-direction: column;
            }
            .links-wrapper {
                padding: 0;
                display: flex;
            }
            .title-wrapper {
                .list-title {
                    font: ${headlineFonts.headline6SemiBoldSm.fontWeight} ${headlineFonts.headline6SemiBoldSm.fontSize} /
                        ${headlineFonts.headline6SemiBoldSm.lineHeight} ${headlineFonts.headline6SemiBoldSm.fontFamily};
                    color: ${colorPallete.grey800};
                }
            }
            .links-wrapper {
                a {
                    font: ${labelFonts.label1Regular.fontWeight} ${labelFonts.label1Regular.fontSize} /
                        ${labelFonts.label1Regular.lineHeight} ${labelFonts.label1Regular.fontFamily};
                    &:hover {
                        span {
                            color: ${colorPallete.primaryMaroon500};
                        }
                    }
                }
            }

            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        null,
                        css`
                            .title-wrapper {
                                .list-title {
                                    font-size: ${headlineFonts.headline6SemiBoldLg.fontSize};
                                    line-height: ${headlineFonts.headline6SemiBoldSm.lineHeight};
                                }
                            }
                        `,
                        css`
                            > div {
                                > div {
                                    min-width: calc(100% / 3 - 27px);
                                    max-width: calc(100% / 3 - 27px);
                                }
                            }
                        `,
                    ],
                    "min",
                )}
        `,
    },
    sectionHeading: {
        css: css`
            font: ${headlineFonts.headline3SemiBoldSm.fontWeight} ${headlineFonts.headline3SemiBoldSm.fontSize} /
                ${headlineFonts.headline3SemiBoldSm.lineHeight} ${headlineFonts.headline3SemiBoldSm.fontFamily};
            color: ${colorPallete.grey800};
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        null,
                        css`
                            font-size: ${headlineFonts.headline3SemiBoldLg.fontSize};
                            line-height: ${headlineFonts.headline3SemiBoldLg.lineHeight};
                        `,
                    ],
                    "min",
                )}
        `,
    },
    needHelpWrapper: {
        css: css`
            display: flex;
            justify-content: center;
        `,
    },
    needHelpCta: {
        css: css`
            display: block;
            text-align: center;
            min-width: 278px;
            width: 100%;
            margin: 0 16px;
            ${({ theme }: { theme: BaseTheme }) =>
            breakpointMediaQueries(
                theme,
                [
                    null,
                    null,
                    css`
                        max-width: 278px;
                        margin: 0;
                    `,
                ],
                "min",
            )}
        `,
    },
};

const styles = dsgQuickLinksSectionWidgetStyles;

const DsgQuickLinksSectionWidget = ({ id, fields }: Props) => {
    return (
        <StyledWrapper {...styles.wrapper}>
            <GridContainer {...styles.columnGridContainer}>
                <GridItem {...styles.leftColumnGridItem}>
                    {fields.quickLinksTitle && (
                        <Typography as="h3" {...styles.sectionHeading}>
                            {fields.quickLinksTitle}
                        </Typography>
                    )}
                    <StyledWrapper {...styles.quickLinksWrapper}>
                        <Zone contentId={id} zoneName="DSGQuickLinks" />
                    </StyledWrapper>
                </GridItem>
                <GridItem {...styles.rightColumnGridItem}>
                    <StyledWrapper>
                        {fields.needHelpTitle && (
                            <Typography as="h3" {...styles.sectionHeading}>
                                {fields.needHelpTitle}
                            </Typography>
                        )}
                        {fields.needHelpCTALabel && fields.needHelpCTALink && (
                            <StyledWrapper {...styles.needHelpWrapper}>
                                <Link
                                    href={useGetLink(fields.needHelpCTALink).url}
                                    className="btn btn-primary"
                                    target={fields.openInNewWindow ? "_blank" : ""}
                                    {...styles.needHelpCta}
                                >
                                    {fields.needHelpCTALabel}
                                </Link>
                            </StyledWrapper>
                        )}
                    </StyledWrapper>
                </GridItem>
            </GridContainer>
        </StyledWrapper>
    );
};

const definition: WidgetDefinition = {
    group: "Common",
    fieldDefinitions: [
        {
            fieldType: "General",
            displayName: "Quick Links Title",
            name: fields.quickLinksTitle,
            editorTemplate: "TextField",
            defaultValue: "Quick Links",
            sortOrder: 1,
        },
        {
            fieldType: "General",
            displayName: "Need Help Title",
            name: fields.needHelpTitle,
            editorTemplate: "TextField",
            defaultValue: "Still Need Help?",
            sortOrder: 2,
        },
        {
            name: fields.needHelpCTALabel,
            displayName: "Need Help CTA Label",
            editorTemplate: "TextField",
            defaultValue: "",
            fieldType: "General",
        },
        {
            name: fields.needHelpCTALink,
            displayName: "Need Help CTA Link",
            editorTemplate: "LinkField",
            defaultValue: { type: "Page", value: "" },
            fieldType: "General",
        },
        {
            name: fields.openInNewWindow,
            editorTemplate: "CheckboxField",
            displayName: "Open Link in New Window",
            defaultValue: false,
            fieldType: "General",
        },
    ],
};

const widgetModule: WidgetModule = {
    component: DsgQuickLinksSectionWidget,
    definition,
};

export default widgetModule;
